import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Hash, ChevronDown, ChevronRight, Copy, ClipboardPaste, RotateCcw, Ruler, Plus, Minus, Save } from "lucide-react";

const QUANTITY_TIERS = [
  { min: 1, max: 9, label: "1–9 szt." },
  { min: 10, max: 19, label: "10–19 szt." },
  { min: 20, max: 29, label: "20–29 szt." },
  { min: 30, max: 39, label: "30–39 szt." },
  { min: 40, max: 49, label: "40–49 szt." },
  { min: 50, max: 75, label: "50–75 szt." },
  { min: 76, max: 99, label: "76–99 szt." },
  { min: 100, max: 149, label: "100–149 szt." },
  { min: 150, max: 199, label: "150–199 szt." },
  { min: 200, max: 299, label: "200–299 szt." },
  { min: 300, max: 499, label: "300–499 szt." },
  { min: 500, max: 749, label: "500–749 szt." },
  { min: 750, max: 999, label: "750–999 szt." },
  { min: 1000, max: 1499, label: "1000–1499 szt." },
  { min: 1500, max: 1999, label: "1500–1999 szt." },
  { min: 2000, max: 2999, label: "2000–2999 szt." },
  { min: 3000, max: 4999, label: "3000–4999 szt." },
  { min: 5000, max: null, label: "5000+ szt." },
];

type FoilInfo = { id: string; foil_key: string; foil_label: string; adjustment_percent: number };
type QtyPricing = {
  id: string;
  foil_id: string;
  quantity_min: number;
  quantity_max: number | null;
  adjustment_percent: number;
};

// Price calculation logic (same as Kalkulator.tsx)
const PRICE_TABLES: Record<number, { areas: number[]; prices: number[] }> = {
  1: { areas: [1, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37, 62.37] },
  50: { areas: [1, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [2.7324, 2.7324, 2.7324, 4.5144, 6.8112, 9.2862, 15.50, 23.00, 34.00, 45.00] },
  100: { areas: [1, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [1.5741, 1.5741, 1.8612, 3.4056, 5.5737, 7.7319, 13.00, 19.50, 29.00, 38.50] },
  200: { areas: [1, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.8811, 0.8811, 1.2276, 2.7819, 4.7421, 6.435, 10.80, 16.20, 24.50, 32.50] },
  300: { areas: [1, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.6831, 0.6831, 1.0692, 2.574, 4.2867, 6.2469, 10.00, 15.00, 22.80, 30.20] },
  500: { areas: [1, 4, 15, 50, 100, 150, 300, 500, 800, 1100], prices: [0.4653, 0.4653, 0.9306, 2.1681, 4.1283, 5.8806, 9.50, 14.20, 21.50, 28.50] },
};
const QTY_LIST = [1, 50, 100, 200, 300, 500];

function lerpVal(x: number, xs: number[], ys: number[]) {
  if (x <= xs[0]) return ys[0];
  if (x >= xs[xs.length - 1]) return ys[ys.length - 1];
  for (let i = 0; i < xs.length - 1; i++) {
    if (xs[i] <= x && x <= xs[i + 1]) {
      const t = (x - xs[i]) / (xs[i + 1] - xs[i]);
      return ys[i] + t * (ys[i + 1] - ys[i]);
    }
  }
  return ys[ys.length - 1];
}

function calcPreviewPrice(wMm: number, hMm: number, qty: number, globalAdjust: number, foilAdjust: number, qtyAdjust: number = 0) {
  if (!wMm || !hMm || wMm < 5 || hMm < 5 || qty < 1) return null;
  const areaCm2 = (wMm / 10) * (hMm / 10);
  function unitForQty(q: number) {
    const tbl = PRICE_TABLES[q];
    return lerpVal(areaCm2, tbl.areas, tbl.prices);
  }
  let unitNet: number;
  if (qty <= 1) {
    unitNet = unitForQty(1);
  } else if (qty < 50) {
    const base50 = unitForQty(50);
    const sqBreaks = [2, 5, 10, 15, 20, 25, 35, 49];
    const sqSurcharges = [220, 164, 76, 45, 32, 21, 17.6, 0];
    const surcharge = lerpVal(qty, sqBreaks, sqSurcharges);
    unitNet = base50 * (1 + surcharge / 100);
  } else {
    const qtyLower = QTY_LIST.filter((q) => q <= qty).pop() || QTY_LIST[0];
    const qtyUpper = QTY_LIST.find((q) => q > qty) || QTY_LIST[QTY_LIST.length - 1];
    if (qtyLower === qtyUpper) {
      unitNet = unitForQty(qtyLower);
    } else {
      const t = (qty - qtyLower) / (qtyUpper - qtyLower);
      unitNet = unitForQty(qtyLower) + t * (unitForQty(qtyUpper) - unitForQty(qtyLower));
    }
  }
  unitNet = unitNet * (1 + globalAdjust / 100) * (1 + foilAdjust / 100) * (1 + qtyAdjust / 100);
  unitNet = Math.round(unitNet * 100) / 100;
  return unitNet;
}

// Unrounded base unit price (without the per-quantity adjustment) — used to convert a target price back into a percent
function calcUnitNetBase(wMm: number, hMm: number, qty: number, globalAdjust: number, foilAdjust: number): number | null {
  if (!wMm || !hMm || wMm < 5 || hMm < 5 || qty < 1) return null;
  const areaCm2 = (wMm / 10) * (hMm / 10);
  function unitForQty(q: number) {
    const tbl = PRICE_TABLES[q];
    return lerpVal(areaCm2, tbl.areas, tbl.prices);
  }
  let unitNet: number;
  if (qty <= 1) {
    unitNet = unitForQty(1);
  } else if (qty < 50) {
    const base50 = unitForQty(50);
    const sqBreaks = [2, 5, 10, 15, 20, 25, 35, 49];
    const sqSurcharges = [220, 164, 76, 45, 32, 21, 17.6, 0];
    const surcharge = lerpVal(qty, sqBreaks, sqSurcharges);
    unitNet = base50 * (1 + surcharge / 100);
  } else {
    const qtyLower = QTY_LIST.filter((q) => q <= qty).pop() || QTY_LIST[0];
    const qtyUpper = QTY_LIST.find((q) => q > qty) || QTY_LIST[QTY_LIST.length - 1];
    if (qtyLower === qtyUpper) {
      unitNet = unitForQty(qtyLower);
    } else {
      const t = (qty - qtyLower) / (qtyUpper - qtyLower);
      unitNet = unitForQty(qtyLower) + t * (unitForQty(qtyUpper) - unitForQty(qtyLower));
    }
  }
  unitNet = unitNet * (1 + globalAdjust / 100) * (1 + foilAdjust / 100);
  return unitNet;
}

// Convert a target price (zł/szt.) into the adjustment percent that produces it
function priceToPercent(targetPrice: number, base: number): number {
  if (!base || base <= 0) return 0;
  return Math.round(((targetPrice / base) - 1) * 10000) / 100;
}

const FoilQuantityPricingSettings = () => {
  const [foils, setFoils] = useState<FoilInfo[]>([]);
  const [qtyPricing, setQtyPricing] = useState<QtyPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [expandedFoils, setExpandedFoils] = useState<Set<string>>(new Set());
  const [globalSlider, setGlobalSlider] = useState(0);
  const [clipboard, setClipboard] = useState<Record<number, string> | null>(null);
  const [globalAdjust, setGlobalAdjust] = useState(0);
  const [defaults, setDefaults] = useState<Record<string, Record<number, string>>>(() => {
    try {
      return JSON.parse(localStorage.getItem("foilQtyDefaults") || "{}");
    } catch { return {}; }
  });
  const [previewW, setPreviewW] = useState("50");
  const [previewH, setPreviewH] = useState("20");

  useEffect(() => {
    fetchAll();
    supabase
      .from("pricing_settings")
      .select("price_adjustment_percent")
      .limit(1)
      .single()
      .then(({ data }) => {
        if (data) setGlobalAdjust(Number(data.price_adjustment_percent));
      });
  }, []);

  const fetchAll = async () => {
    const [foilRes, qtyRes] = await Promise.all([
      supabase.from("foil_pricing").select("id, foil_key, foil_label, adjustment_percent").order("foil_label"),
      supabase.from("foil_quantity_pricing").select("*").order("quantity_min"),
    ]);
    if (foilRes.data) setFoils(foilRes.data as FoilInfo[]);
    if (qtyRes.data) {
      setQtyPricing(qtyRes.data as QtyPricing[]);
      const vals: Record<string, string> = {};
      (qtyRes.data as QtyPricing[]).forEach((q) => {
        vals[q.id] = String(q.adjustment_percent);
      });
      setEditValues(vals);
      setSavedValues(vals);
    }
    setLoading(false);
  };

  const getTierRecord = (foilId: string, tierMin: number) => {
    return qtyPricing.find((q) => q.foil_id === foilId && q.quantity_min === tierMin);
  };

  const handleSaveFoil = async (foilId: string) => {
    setSaving(true);
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const updates = foilTiers.map((t) => {
      const newVal = parseFloat(editValues[t.id]) || 0;
      return supabase
        .from("foil_quantity_pricing")
        .update({ adjustment_percent: newVal, updated_at: new Date().toISOString() })
        .eq("id", t.id);
    });
    await Promise.all(updates);
    await fetchAll();
    toast.success("Progi ilościowe zapisane");
    setSaving(false);
  };

  const handleFillAll = (foilId: string, value: string) => {
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const newVals = { ...editValues };
    foilTiers.forEach((t) => {
      newVals[t.id] = value;
    });
    setEditValues(newVals);
  };

  const handleCopy = (foilId: string) => {
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const copied: Record<number, string> = {};
    foilTiers.forEach((t) => {
      copied[t.quantity_min] = editValues[t.id] ?? "0";
    });
    setClipboard(copied);
    toast.success("Skopiowano progi");
  };

  const handlePaste = (foilId: string) => {
    if (!clipboard) { toast.error("Najpierw skopiuj progi z innej folii"); return; }
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const newVals = { ...editValues };
    foilTiers.forEach((t) => {
      if (clipboard[t.quantity_min] !== undefined) {
        newVals[t.id] = clipboard[t.quantity_min];
      }
    });
    setEditValues(newVals);
    toast.success("Wklejono progi");
  };

  const handleSaveDefaults = (foilId: string) => {
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const def: Record<number, string> = {};
    foilTiers.forEach((t) => { def[t.quantity_min] = editValues[t.id] ?? "0"; });
    const newDefaults = { ...defaults, [foilId]: def };
    setDefaults(newDefaults);
    localStorage.setItem("foilQtyDefaults", JSON.stringify(newDefaults));
    toast.success("Zapisano ustawienia domyślne");
  };

  const handleReset = (foilId: string) => {
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const newVals = { ...editValues };
    const def = defaults[foilId];
    foilTiers.forEach((t) => {
      newVals[t.id] = def ? (def[t.quantity_min] ?? "0") : "0";
    });
    setEditValues(newVals);
    toast.success(def ? "Przywrócono ustawienia domyślne" : "Zresetowano do 0%");
  };

  const handleAdjustAll = (foilId: string, delta: number) => {
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const newVals = { ...editValues };
    foilTiers.forEach((t) => {
      newVals[t.id] = String(Math.round(((parseFloat(newVals[t.id]) || 0) + delta) * 100) / 100);
    });
    setEditValues(newVals);
  };

  // Adjust the actual price (zł/szt.) of every tier of a foil by deltaPrice (e.g. +/- 0.01 zł)
  const handleAdjustAllPrice = (foilId: string, deltaPrice: number) => {
    const foil = foils.find((f) => f.id === foilId);
    if (!foil) return;
    const w = parseInt(previewW) || 50;
    const h = parseInt(previewH) || 20;
    const foilTiers = qtyPricing.filter((q) => q.foil_id === foilId);
    const newVals = { ...editValues };
    foilTiers.forEach((t) => {
      const base = calcUnitNetBase(w, h, t.quantity_min, globalAdjust, foil.adjustment_percent);
      if (base === null || base <= 0) return;
      const cur = parseFloat(newVals[t.id]) || 0;
      const price = Math.round(base * (1 + cur / 100) * 100) / 100;
      const np = Math.max(0, Math.round((price + deltaPrice) * 100) / 100);
      newVals[t.id] = String(priceToPercent(np, base));
    });
    setEditValues(newVals);
  };


  const wMm = parseInt(previewW) || 50;
  const hMm = parseInt(previewH) || 20;

  if (loading) return <p className="text-muted-foreground text-sm">Ładowanie progów ilościowych...</p>;

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5">
      <div className="flex items-center gap-3">
        <Hash className="w-5 h-5 text-accent" />
        <h2 className="text-lg font-bold text-foreground">Progi rabatowe wg ilości (per folia)</h2>
      </div>

      <p className="text-muted-foreground text-sm">
        Reguluj cenę za sztukę (zł netto) dla każdego progu ilościowego. Klikaj <span className="font-bold">+</span> / <span className="font-bold">–</span>, aby zmienić cenę o 1 grosz, albo wpisz cenę ręcznie. Cena zależy od rozmiaru podanego w podglądzie poniżej.

      </p>

      {/* Global adjustment for all foils */}
      <div className="flex flex-wrap items-center gap-3 bg-muted/50 rounded-lg p-3 border border-border">
        <span className="text-sm font-medium text-foreground">Zbiorcza korekta wszystkich folii:</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => {
              const delta = -0.5;
              setGlobalSlider((prev) => Math.round((prev - 0.5) * 100) / 100);
              const newVals = { ...editValues };
              qtyPricing.forEach((t) => {
                newVals[t.id] = String(Math.round(((parseFloat(newVals[t.id]) || 0) + delta) * 100) / 100);
              });
              setEditValues(newVals);
            }}
            className="w-7 h-7 flex items-center justify-center rounded border border-border bg-background hover:bg-destructive/10 hover:border-destructive/30 text-muted-foreground hover:text-destructive transition-colors"
            title="Zmniejsz wszystkie o 0.5%"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <Input
            type="number"
            step="0.5"
            value={globalSlider}
            onChange={(e) => setGlobalSlider(parseFloat(e.target.value) || 0)}
            className="w-16 h-7 text-center text-xs"
          />
          <button
            onClick={() => {
              const delta = 0.5;
              setGlobalSlider((prev) => Math.round((prev + 0.5) * 100) / 100);
              const newVals = { ...editValues };
              qtyPricing.forEach((t) => {
                newVals[t.id] = String(Math.round(((parseFloat(newVals[t.id]) || 0) + delta) * 100) / 100);
              });
              setEditValues(newVals);
            }}
            className="w-7 h-7 flex items-center justify-center rounded border border-border bg-background hover:bg-accent/10 hover:border-accent/30 text-muted-foreground hover:text-accent transition-colors"
            title="Zwiększ wszystkie o 0.5%"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <span className={`text-xs font-bold ${globalSlider > 0 ? "text-green-600" : globalSlider < 0 ? "text-red-600" : "text-muted-foreground"}`}>
          {globalSlider > 0 ? "+" : ""}{globalSlider}%
        </span>
        <span className="w-px h-4 bg-border mx-1" />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditValues({ ...savedValues });
            toast.success("Przywrócono ostatnio zapisane ustawienia");
          }}
          className="text-xs h-7"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Wróć do zapisanych
        </Button>
        {(() => {
          const hasUnsaved = Object.keys(savedValues).some(
            (k) => (editValues[k] ?? "0") !== (savedValues[k] ?? "0")
          );
          return (
            <Button
              size="sm"
              variant={hasUnsaved ? "destructive" : "outline"}
              disabled={saving || !hasUnsaved}
              onClick={async () => {
                setSaving(true);
                const updates = qtyPricing.map((t) => {
                  const newVal = parseFloat(editValues[t.id]) || 0;
                  return supabase
                    .from("foil_quantity_pricing")
                    .update({ adjustment_percent: newVal, updated_at: new Date().toISOString() })
                    .eq("id", t.id);
                });
                await Promise.all(updates);
                await fetchAll();
                toast.success("Wszystkie progi zapisane");
                setSaving(false);
              }}
              className={`text-xs h-7 ${hasUnsaved ? "animate-pulse" : ""}`}
            >
              <Save className="w-3 h-3 mr-1" />
              {saving ? "Zapisywanie..." : hasUnsaved ? "Zapisz zmiany!" : "Zapisano"}
            </Button>
          );
        })()}
      </div>

      {/* Preview size settings */}
      <div className="flex flex-wrap items-center gap-3 bg-muted/50 rounded-lg p-3 border border-border">
        <Ruler className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-muted-foreground">Podgląd ceny dla naklejki:</span>
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            value={previewW}
            onChange={(e) => setPreviewW(e.target.value)}
            className="w-16 h-7 text-center text-xs"
            min={5}
            max={380}
          />
          <span className="text-xs text-muted-foreground">×</span>
          <Input
            type="number"
            value={previewH}
            onChange={(e) => setPreviewH(e.target.value)}
            className="w-16 h-7 text-center text-xs"
            min={5}
            max={280}
          />
          <span className="text-xs text-muted-foreground">mm</span>
        </div>
      </div>

      <div className="space-y-2">
        {foils.map((foil) => {
          const isExpanded = expandedFoils.has(foil.id);
          const foilTiers = qtyPricing.filter((q) => q.foil_id === foil.id);

          return (
            <div key={foil.id} className="border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedFoils(prev => { const next = new Set(prev); if (next.has(foil.id)) next.delete(foil.id); else next.add(foil.id); return next; })}
                className="w-full flex items-center gap-3 p-3 bg-background hover:bg-muted/50 transition-colors text-left"
              >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                <span className="text-sm font-medium text-foreground flex-1">{foil.foil_label}</span>
                <span className="text-xs text-muted-foreground">{foilTiers.length} progów</span>
              </button>

              {isExpanded && (
                <div className="p-3 bg-muted/20 space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Wszystkie ceny:</span>
                    <button
                      onClick={() => handleAdjustAllPrice(foil.id, -0.01)}
                      className="w-7 h-7 flex items-center justify-center rounded border border-border bg-background hover:bg-destructive/10 hover:border-destructive/30 text-muted-foreground hover:text-destructive transition-colors"
                      title="Obniż wszystkie ceny o 1 grosz"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleAdjustAllPrice(foil.id, 0.01)}
                      className="w-7 h-7 flex items-center justify-center rounded border border-border bg-background hover:bg-accent/10 hover:border-accent/30 text-muted-foreground hover:text-accent transition-colors"
                      title="Podnieś wszystkie ceny o 1 grosz"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleAdjustAllPrice(foil.id, -0.10)} className="text-xs px-2 py-1 rounded border border-border bg-background hover:border-accent/50 text-muted-foreground transition-colors">−10 gr</button>
                    <button onClick={() => handleAdjustAllPrice(foil.id, 0.10)} className="text-xs px-2 py-1 rounded border border-border bg-background hover:border-accent/50 text-muted-foreground transition-colors">+10 gr</button>
                    <span className="w-px h-4 bg-border mx-1" />

                    <button onClick={() => handleCopy(foil.id)} className="text-xs px-2 py-1 rounded border border-border bg-background hover:border-accent/50 text-muted-foreground transition-colors flex items-center gap-1">
                      <Copy className="w-3 h-3" /> Kopiuj
                    </button>
                    <button onClick={() => handlePaste(foil.id)} disabled={!clipboard} className="text-xs px-2 py-1 rounded border border-border bg-background hover:border-accent/50 text-muted-foreground transition-colors flex items-center gap-1 disabled:opacity-40">
                      <ClipboardPaste className="w-3 h-3" /> Wklej
                    </button>
                    <button onClick={() => handleReset(foil.id)} className="text-xs px-2 py-1 rounded border border-border bg-background hover:border-accent/50 text-muted-foreground transition-colors flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Resetuj{defaults[foil.id] ? " do domyślnych" : ""}
                    </button>
                    <button onClick={() => handleSaveDefaults(foil.id)} className="text-xs px-2 py-1 rounded border border-accent/50 bg-accent/10 hover:bg-accent/20 text-accent transition-colors flex items-center gap-1">
                      <Save className="w-3 h-3" /> Zapisz jako domyślne
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {QUANTITY_TIERS.map((tier) => {
                      const record = getTierRecord(foil.id, tier.min);
                      if (!record) return null;
                      const val = parseFloat(editValues[record.id]) || 0;
                      const base = calcUnitNetBase(wMm, hMm, tier.min, globalAdjust, foil.adjustment_percent);
                      const price = base !== null ? Math.round(base * (1 + val / 100) * 100) / 100 : null;
                      const setPrice = (newPrice: number) => {
                        if (base === null || base <= 0) return;
                        const np = Math.max(0, Math.round(newPrice * 100) / 100);
                        setEditValues((prev) => ({ ...prev, [record.id]: String(priceToPercent(np, base)) }));
                      };
                      return (
                        <div key={record.id} className="flex items-center gap-1.5 bg-background rounded-lg p-2 border border-border">
                          <span className="text-xs text-muted-foreground w-28 shrink-0">{tier.label}</span>
                          <button
                            onClick={() => price !== null && setPrice(price - 0.01)}
                            disabled={price === null}
                            className="w-6 h-6 flex items-center justify-center rounded border border-border bg-muted hover:bg-destructive/10 hover:border-destructive/30 text-muted-foreground hover:text-destructive transition-colors shrink-0 disabled:opacity-40"
                            title="−1 grosz"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={price !== null ? price.toFixed(2) : ""}
                            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                            className="w-20 h-7 text-center text-xs font-bold"
                          />
                          <span className="text-xs text-muted-foreground shrink-0">zł/szt.</span>
                          <button
                            onClick={() => price !== null && setPrice(price + 0.01)}
                            disabled={price === null}
                            className="w-6 h-6 flex items-center justify-center rounded border border-border bg-muted hover:bg-accent/10 hover:border-accent/30 text-muted-foreground hover:text-accent transition-colors shrink-0 disabled:opacity-40"
                            title="+1 grosz"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>


                  <Button
                    size="sm"
                    onClick={() => handleSaveFoil(foil.id)}
                    disabled={saving}
                  >
                    {saving ? "Zapisywanie..." : "Zapisz progi dla tej folii"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FoilQuantityPricingSettings;
